import type { ComboboxData } from '@mantine/core';

/**
 * Validates and normalizes data for Mantine's MultiSelect component
 * Handles nested group structures
 *
 * @param rawData - The raw data to validate (any type)
 * @returns A safely formatted ComboboxData array
 */
const validateMultiSelectData = (rawData: any): ComboboxData => {
  try {
    if (!rawData) return [];

    if (Array.isArray(rawData)) {
      return rawData
        .filter((item) => item !== null && item !== undefined)
        .map((item) => {
          // If item is a string, return as is
          if (typeof item === 'string') return item;

          // If item is an object
          if (typeof item === 'object' && item !== null) {
            // Handle items with value/label
            if ('value' in item) {
              return {
                value: String(item.value || ''),
                label: item.label !== undefined ? String(item.label) : String(item.value || ''),
                ...(item.group !== undefined ? { group: String(item.group) } : {}),
                ...(item.disabled !== undefined ? { disabled: Boolean(item.disabled) } : {}),
              };
            }

            // Handle grouped items
            if ('group' in item && Array.isArray(item.items)) {
              const safeItems = item.items
                .filter((subItem: any) => subItem !== null && subItem !== undefined)
                .map((subItem: any) => {
                  if (typeof subItem === 'string') return subItem;
                  if (typeof subItem === 'object' && subItem !== null && 'value' in subItem) {
                    return {
                      value: String(subItem.value || ''),
                      label: subItem.label !== undefined ? String(subItem.label) : String(subItem.value || ''),
                      ...(subItem.disabled !== undefined ? { disabled: Boolean(subItem.disabled) } : {}),
                    };
                  }
                  return null;
                })
                .filter(Boolean);

              // Only return groups with valid items
              if (safeItems.length === 0) return null;

              return {
                group: String(item.group || ''),
                items: safeItems,
              };
            }
          }

          return null;
        })
        .filter(Boolean) as ComboboxData;
    }

    return [];
  } catch (error) {
    console.error('Error validating multiselect data:', error);
    return [];
  }
};

/**
 * Validates and safely formats a multiselect value array
 *
 * @param value - The value array to validate (any type)
 * @returns A string array (empty if invalid)
 */
const validateMultiSelectValue = (value: any): string[] => {
  try {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && typeof v === 'string' && v !== '')
        .map((v) => String(v));
    }

    return [];
  } catch (error) {
    console.error('Error validating multiselect value:', error);
    return [];
  }
};

/**
 * Creates a safe onChange handler for MultiSelect component
 * Ensures proper error handling and value normalization
 *
 * @param handler - The original onChange handler
 * @returns A wrapped handler that safely calls the original
 */
const createSafeMultiSelectChangeHandler = (
  handler: ((value: string[]) => void) | undefined,
): ((value: string[]) => void) => {
  return (value) => {
    try {
      if (handler) {
        handler(Array.isArray(value) ? value.filter((v) => typeof v === 'string') : []);
      }
    } catch (error) {
      console.error('Error in multiselect onChange:', error);
    }
  };
};

// Export all helpers as a single object
export { createSafeMultiSelectChangeHandler, validateMultiSelectData, validateMultiSelectValue };
